(() => {
  'use strict';

  if (document.__dimHeroCarouselBootstrap) return;
  document.__dimHeroCarouselBootstrap = true;

  const selector = '[data-home-hero-carousel]';
  const transitionMs = 350;

  function mount(root) {
    if (root.__dimHeroCarousel) return;
    const doc = root.ownerDocument || document;
    const win = doc.defaultView || window;
    const slides = Array.from(root.querySelectorAll('[data-hero-slide]'));
    if (!slides.length) return;
    const images = slides.map((slide) => slide.querySelector('img'));
    const controls = root.querySelector('[data-hero-controls]');
    const play = root.querySelector('[data-hero-play]');
    const previous = root.querySelector('[data-hero-prev]');
    const next = root.querySelector('[data-hero-next]');
    const pickers = Array.from(root.querySelectorAll('[data-hero-picker]'));
    const status = root.querySelector('[data-hero-status]');
    const reducedMotion = win.matchMedia?.('(prefers-reduced-motion: reduce)');
    const interval = Math.max(1000, Number(root.dataset.interval) || 6000);
    const failed = new Set();
    const cleanups = [];
    let active = slides.findIndex((slide) => !slide.hidden);
    if (active < 0) active = 0;
    let pending = null;
    let timer = null;
    let fadeTimer = null;
    let inView = !win.IntersectionObserver;
    const manualStory = root.dataset.manualStory === 'true';
    let paused = manualStory || root.dataset.autoplay !== 'true' || Boolean(reducedMotion?.matches);
    let focusPaused = false;
    let ignorePlayFocusOnce = false;
    let pointerPlayFocus = false;
    let hovering = false;
    let destroyed = false;

    const listen = (target, event, handler) => {
      target?.addEventListener?.(event, handler);
      cleanups.push(() => target?.removeEventListener?.(event, handler));
    };
    const available = () => slides.map((_slide, index) => index).filter((index) => !failed.has(index));
    const ready = (index) => Boolean(images[index]?.complete && images[index].naturalWidth > 0);
    const visible = () => inView && !doc.hidden;
    const stopTimer = () => { if (timer !== null) win.clearTimeout(timer); timer = null; };
    const finishFade = () => {
      if (fadeTimer !== null) win.clearTimeout(fadeTimer);
      fadeTimer = null;
      slides.forEach((slide, index) => {
        slide.classList.remove('is-entering', 'is-visible', 'is-leaving');
        slide.hidden = index !== active;
        if (index === active) { slide.removeAttribute('aria-hidden'); slide.removeAttribute('inert'); }
        else { slide.setAttribute('aria-hidden', 'true'); slide.setAttribute('inert', ''); }
      });
    };
    const updateControls = () => {
      const usable = available();
      root.setAttribute('data-hero-count', String(usable.length));
      if (controls) controls.hidden = usable.length < 2;
      if (play) {
        const label = paused || focusPaused ? play.dataset.playLabel || 'Play' : play.dataset.pauseLabel || 'Pause';
        play.textContent = paused || focusPaused ? '▶ Play' : '❚❚ Pause';
        play.setAttribute('aria-label', label);
      }
      pickers.forEach((picker) => {
        const index = Number(picker.dataset.heroIndex);
        picker.hidden = failed.has(index);
        picker.disabled = failed.has(index);
        picker.setAttribute('aria-current', index === active ? 'true' : 'false');
        if (usable.includes(index)) {
          picker.setAttribute('aria-label', `Show example ${usable.indexOf(index) + 1}: ${slides[index].dataset.heroSlideName || ''}`);
        }
      });
    };
    const schedule = () => {
      stopTimer();
      if (manualStory || destroyed || paused || focusPaused || hovering || !visible() || available().length < 2 || pending !== null || !ready(active)) return;
      timer = win.setTimeout(() => {
        timer = null;
        const usable = available();
        const position = usable.indexOf(active);
        request(usable[(position + 1) % usable.length], false);
      }, interval);
    };
    const announce = () => {
      if (!status) return;
      const usable = available();
      status.textContent = `Example ${usable.indexOf(active) + 1} of ${usable.length}: ${slides[active].dataset.heroSlideName || ''}`;
    };
    const show = (index, manual) => {
      pending = null;
      if (index === active) { if (manual) announce(); schedule(); return; }
      finishFade();
      const old = slides[active];
      const incoming = slides[index];
      active = index;
      incoming.hidden = false;
      incoming.removeAttribute('aria-hidden');
      incoming.removeAttribute('inert');
      if (!reducedMotion?.matches) {
        incoming.classList.add('is-entering');
        // Force the initial opacity to apply before the visible transition begins.
        void incoming.offsetWidth;
        incoming.classList.add('is-visible');
        old.classList.add('is-leaving');
        old.setAttribute('aria-hidden', 'true');
        old.setAttribute('inert', '');
        fadeTimer = win.setTimeout(finishFade, transitionMs);
      } else {
        old.hidden = true;
        old.setAttribute('aria-hidden', 'true');
        old.setAttribute('inert', '');
      }
      if (manual) announce();
      updateControls();
      schedule();
    };
    const request = (index, manual) => {
      if (manual) { paused = true; focusPaused = false; stopTimer(); }
      if (!Number.isInteger(index) || failed.has(index) || index < 0 || index >= slides.length) { updateControls(); schedule(); return; }
      if (ready(index)) show(index, manual);
      else {
        images[index].loading = 'eager';
        pending = { index, manual };
        stopTimer();
      }
      updateControls();
    };
    const move = (direction) => {
      const usable = available();
      if (usable.length < 2) return;
      const position = usable.indexOf(active);
      request(usable[(position + direction + usable.length) % usable.length], true);
    };
    const fail = (index) => {
      if (failed.has(index)) return;
      failed.add(index);
      if (pending?.index === index) pending = null;
      if (index === active) {
        const replacement = available().find(ready);
        if (replacement !== undefined) show(replacement, false);
        else {
          const next = available()[0];
          if (next !== undefined && images[next]) {
            images[next].loading = 'eager';
            pending = { index: next, manual: false };
          }
        }
      }
      updateControls();
      schedule();
    };

    images.forEach((image, index) => {
      if (!image) { fail(index); return; }
      listen(image, 'load', () => {
        if (image.naturalWidth <= 0) { fail(index); return; }
        if (pending?.index === index) {
          const chosen = pending;
          if (chosen.manual || visible() || failed.has(active)) show(index, chosen.manual);
          else { pending = null; schedule(); }
        } else if (failed.has(active)) show(index, false);
        else if (index === active) schedule();
      });
      listen(image, 'error', () => fail(index));
      if (image.complete && image.naturalWidth <= 0) fail(index);
    });
    listen(play, 'pointerdown', () => { pointerPlayFocus = true; });
    listen(play, 'pointercancel', () => { pointerPlayFocus = false; });
    listen(play, 'click', () => {
      pointerPlayFocus = false;
      if (paused || focusPaused) { paused = false; focusPaused = false; ignorePlayFocusOnce = true; }
      else paused = true;
      updateControls();
      schedule();
    });
    listen(previous, 'click', () => move(-1));
    listen(next, 'click', () => move(1));
    pickers.forEach((picker) => listen(picker, 'click', () => request(Number(picker.dataset.heroIndex), true)));
    listen(root, 'focusin', (event) => {
      if (root.contains(event.relatedTarget)) return;
      if (event.target === play && (ignorePlayFocusOnce || pointerPlayFocus)) { ignorePlayFocusOnce = false; return; }
      focusPaused = true;
      stopTimer();
      updateControls();
    });
    listen(root, 'focusout', (event) => {
      if (!root.contains(event.relatedTarget)) { ignorePlayFocusOnce = false; pointerPlayFocus = false; }
    });
    listen(root, 'pointerenter', () => { hovering = true; stopTimer(); });
    listen(root, 'pointerleave', () => { hovering = false; schedule(); });
    listen(doc, 'visibilitychange', () => {
      if (doc.hidden) { stopTimer(); if (pending && !pending.manual) pending = null; }
      else schedule();
    });
    listen(reducedMotion, 'change', () => {
      if (!reducedMotion.matches) return;
      paused = true;
      stopTimer();
      if (pending && !pending.manual) pending = null;
      finishFade();
      updateControls();
    });
    let observer;
    if (win.IntersectionObserver) {
      observer = new win.IntersectionObserver((entries) => {
        inView = Boolean(entries[0]?.isIntersecting);
        if (!inView) { stopTimer(); if (pending && !pending.manual) pending = null; }
        else schedule();
      });
      observer.observe(root);
    }
    listen(doc, 'shopify:block:select', (event) => {
      const index = slides.findIndex((slide) => slide.contains(event.target));
      if (index < 0 || failed.has(index)) return;
      paused = true; focusPaused = false;
      request(index, true);
    });
    listen(doc, 'shopify:section:select', (event) => {
      if (event.target !== root && !event.target?.contains(root)) return;
      paused = true; stopTimer(); updateControls();
    });
    const destroy = () => {
      destroyed = true;
      stopTimer();
      finishFade();
      observer?.disconnect();
      cleanups.forEach((cleanup) => cleanup());
      delete root.__dimHeroCarousel;
    };
    listen(doc, 'shopify:section:unload', (event) => {
      if (event.target === root || event.target?.contains(root)) destroy();
    });
    root.__dimHeroCarousel = { destroy, showStory: index => request(index, true) };
    updateControls();
    schedule();
  }

  function mountEntry(section) {
    if (section.__dimEEntry) return;
    if ((section.hasAttribute?.('data-dim-offer-preview') || section.hasAttribute?.('data-dim-offer-public')) && !section.__dimOfferReady) return;
    const form = section.querySelector('[data-e-entry-form]');
    const idea = section.querySelector('[data-e-entry-idea]');
    const error = section.querySelector('[data-e-entry-error]');
    const feedback = section.querySelector('[data-e-entry-feedback]');
    const replacement = section.querySelector('[data-e-entry-example-replace]');
    const announcement = section.querySelector('[data-e-entry-example-announcement]');
    if (!form || !idea) return;
    const doc = section.ownerDocument || document, win = doc.defaultView || window;
    let navigating = false, disposed = false;
    let retainedIdea = idea.value.trim();
    let lastExampleText = '', pendingExample = null;
    const cancelExample = () => { pendingExample = null; if (replacement) replacement.hidden = true; };
    const fillExample = control => {
      idea.value = control.dataset.eEntryExample;
      lastExampleText = idea.value;
      clearEmpty(); cancelExample();
      if (announcement) announcement.textContent = announcement.dataset.addedLabel || 'Example added';
    };
    const showError = (text, code = 'operation') => { if (error) { error.textContent = text; error.dataset.code = code; error.hidden = !text; } };
    const clearEmpty = () => { if (error?.dataset.code === 'empty') showError(''); };
    const flushForDeparture = async () => {
      if (disposed) return true;
      const rawText = idea.value.trim();
      if (rawText === retainedIdea) return true;
      try {
        const intake = win.DIMSourceIntake;
        if (!intake?.read || !intake.stage) throw new Error('ENTRY_UNAVAILABLE');
        const current = await intake.read();
        if (current.continuationStatus === 'expired') throw new Error('ENTRY_EXPIRED');
        if (current.continuation?.rawText !== rawText) {
          await intake.stage({ ...(!current.continuation ? { intent: 'guided' } : {}), rawText }, { expectedRevision: current.revision });
          const retained = await intake.read();
          if (retained.continuationStatus !== 'ready' || retained.continuation?.rawText !== rawText) throw new Error('ENTRY_NOT_SAVED');
        }
        if (disposed || idea.value.trim() !== rawText) throw new Error('ENTRY_CHANGED');
        retainedIdea = rawText;
        return true;
      } catch (_) {
        showError('Your latest idea could not be saved. Stay here and try again before leaving; your current idea is still on this page.');
        return false;
      }
    };
    const onBeforeJourneyLeave = event => event.detail?.waitUntil?.(flushForDeparture());
    const onSectionUnload = event => {
      if (event.target !== section && !event.target?.contains?.(section)) return;
      disposed = true;
      doc.removeEventListener('dim:before-journey-leave', onBeforeJourneyLeave);
      doc.removeEventListener('shopify:section:unload', onSectionUnload);
      delete section.__dimEEntry;
    };
    doc.addEventListener('dim:before-journey-leave', onBeforeJourneyLeave);
    doc.addEventListener('shopify:section:unload', onSectionUnload);
    const navigate = async (intent, href, submitGuided = false) => {
      if (navigating) return;
      navigating = true;
      if (submitGuided && feedback) { feedback.textContent = 'Preparing your suggestions…'; feedback.hidden = false; }
      try {
        if (!win.DIMSourceIntake?.continue || !win.DreamItJourneyRoutes?.navigateJourney) throw new Error('ENTRY_UNAVAILABLE');
        let selection;
        if (section.__dimOfferReady) {
          await win.DIMRuntime.verify({ force: true });
          selection = win.DIMStorefrontDisplay.singleSelection(win.DIMRuntime.display) || undefined;
          if (selection && ['upload', 'create', 'choose'].includes(intent)) {
            const exact = new URL(href, win.location.origin);
            exact.searchParams.set('product', selection.productId); exact.searchParams.set('variant', selection.variantId);
            href = exact.href;
          }
        }
        await win.DIMSourceIntake.continue({ intent, rawText: idea.value.trim(), ...(selection ? { selection } : {}), ...(submitGuided ? { submitGuided: true, descriptionOnly: true } : {}) });
        const moved = await win.DreamItJourneyRoutes.navigateJourney({ url: href, runtime: win.DIMRuntime, location: win.location,
          assign: win.location.assign.bind(win.location), family: intent === 'guided' ? 'guided' : intent === 'choose' ? 'product' : 'source' });
        if (moved === false) throw new Error('ENTRY_UNAVAILABLE');
      } catch (_) {
        if (feedback && submitGuided) feedback.hidden = true;
        showError('This route could not be opened. Your idea and existing design are retained.');
      }
      finally { navigating = false; }
    };
    idea.addEventListener('input', () => { cancelExample(); if (idea.value.trim()) clearEmpty(); });
    form.addEventListener('submit', event => {
      event.preventDefault();
      if (navigating) return;
      if (!idea.value.trim()) { showError('Tell us your idea to get started.', 'empty'); return; }
      clearEmpty();
      const destination = new URL(section.dataset.guidedUrl, win.location.origin);
      void navigate('guided', destination.href, true);
    });
    section.querySelectorAll('[data-e-entry-direct]').forEach(control => control.addEventListener('click', event => {
      if (!win.DIMSourceIntake?.continue || !win.DreamItJourneyRoutes?.isJourneyActivation?.(event, control)) return;
      event.preventDefault(); void navigate(control.dataset.eEntryDirect, control.href);
    }));
    section.querySelectorAll('[data-e-entry-example]').forEach(control => control.addEventListener('click', () => {
      if (navigating) return;
      // An example can replace another untouched example. Customer edits and
      // restored wording need a deliberate inline choice before replacement.
      if (idea.value.trim() && idea.value !== lastExampleText) {
        pendingExample = control;
        if (replacement) replacement.hidden = false;
        return;
      }
      fillExample(control);
    }));
    section.querySelector('[data-e-entry-example-confirm]')?.addEventListener('click', () => {
      if (!pendingExample || navigating) return;
      const control = pendingExample;
      fillExample(control);
      control.focus?.({ preventScroll: true });
    });
    section.querySelector('[data-e-entry-example-cancel]')?.addEventListener('click', () => {
      const control = pendingExample;
      cancelExample();
      control?.focus?.({ preventScroll: true });
    });
    const toggle = section.querySelector('[data-e-entry-examples-toggle]');
    const moreExamples = section.querySelector('[data-e-entry-more-examples]');
    toggle?.addEventListener('click', () => {
      if (!moreExamples) return;
      const expanded = toggle.getAttribute('aria-expanded') !== 'true';
      moreExamples.hidden = !expanded;
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.textContent = expanded ? toggle.dataset.fewerLabel : toggle.dataset.moreLabel;
      cancelExample();
    });
    section.__dimEEntry = true;
    void Promise.resolve().then(() => win.DIMSourceIntake?.read?.()).then(retained => {
      const entry = retained?.continuation;
      if (!entry || disposed || idea.value || retained.continuationStatus === 'expired') return;
      idea.value = entry.rawText || '';
      retainedIdea = idea.value.trim();
    }).catch(() => {});
  }

  async function mountOffer(section) {
    if (section.__dimOfferLoading) return;
    section.__dimOfferLoading = true;
    const doc = section.ownerDocument || document, win = doc.defaultView || window;
    const status = doc.querySelector('[data-dim-offer-status]');
    try {
      const runtime = win.DIMRuntime;
      const publicOffer = section.hasAttribute('data-dim-offer-public');
      if (!publicOffer && (runtime?.runtime?.environment !== 'development' || runtime.continuation?.provisionalContext?.purpose !== 'staff-preview')) throw new Error('PREVIEW_REQUIRED');
      const context = await runtime.verify();
      const display = runtime.display;
      if (!win.DIMStorefrontDisplay.focusedContext(runtime, context)
        || publicOffer && (context.mode !== 'alias' || context.purpose !== 'customer')
        || !publicOffer && context.purpose !== 'staff-preview'
        || !display || typeof display.displayName !== 'string' || !display.details) throw new Error('PREVIEW_REQUIRED');
      const single = win.DIMStorefrontDisplay.singleSelection(display);
      const details = display.details;
      const text = (selector, value) => { const node = section.querySelector(selector); if (node && typeof value === 'string' && value.trim()) node.textContent = value; };
      text('h1', details.headline || display.displayName);
      text('.ux-home-hero__eyebrow', display.displayName);
      text('.ux-home-hero__lead', details.introduction || details.summary || details.audience || '');
      text('[data-e-entry-form] label', details.promptLabel);
      text('[data-e-entry-form] button[type="submit"]', details.primaryActionLabel);
      const extraCopy = section.querySelector('[data-dim-offer-copy]');
      if (extraCopy) extraCopy.hidden = true;
      const exampleButtons = Array.from(section.querySelectorAll('[data-e-entry-example]'));
      const examples = Array.isArray(details.examples) ? details.examples.filter(value => typeof value === 'string' && value.trim()).slice(0, exampleButtons.length) : [];
      exampleButtons.forEach((button, index) => {
        button.hidden = !examples[index];
        if (examples[index]) { button.dataset.eEntryExample = examples[index]; button.textContent = examples[index]; }
      });
      const examplesPanel = section.querySelector('.ux-home-hero__examples');
      if (examplesPanel) examplesPanel.hidden = !examples.length;
      const toggle = section.querySelector('[data-e-entry-examples-toggle]'); if (toggle) toggle.hidden = examples.length <= 3;
      const choose = section.querySelector('[data-e-entry-direct="choose"]'); if (choose) choose.hidden = Boolean(single);
      if (single) section.dataset.dimSingleOffer = 'true';
      const media = win.DIMStorefrontDisplay.resolveMedia(display.assets);
      const hero = media.find(item => item.kind === 'hero') || media.find(item => item.kind === 'product');
      const story = section.querySelector('[data-home-hero-story]');
      if (story) {
        story.hidden = false;
        const picture = story.querySelector('picture'); if (picture) picture.hidden = !hero;
        const ideaLine = story.querySelector('.ux-home-hero__story-idea'); if (ideaLine) ideaLine.hidden = Boolean(hero);
        if (hero) {
          story.querySelectorAll('source').forEach(node => node.remove());
          const image = story.querySelector('img');
          if (image) { image.src = hero.url; image.alt = hero.alt || display.displayName; }
          text('[data-home-hero-story] h2', display.displayName);
          text('[data-home-hero-story] figcaption strong', hero.alt || display.displayName);
          const label = story.querySelector('figcaption small'); if (label) label.textContent = 'Product image';
          const explanation = story.querySelector('figcaption p'); if (explanation) explanation.hidden = true;
        } else {
          text('[data-home-hero-story] h2', display.displayName);
          text('.ux-home-hero__story-idea', publicOffer ? 'A product image is not available for this storefront.' : 'A product image is not available for this saved preview.');
          const caption = story.querySelector('figcaption'); if (caption) caption.hidden = true;
        }
      }
      doc.querySelectorAll('[data-dim-store-name]').forEach(node => { node.textContent = display.displayName; node.hidden = false; });
      section.__dimOfferReady = true;
      section.hidden = false;
      if (publicOffer) section.removeAttribute('inert');
      if (status) { status.textContent = 'Saved storefront preview'; status.hidden = true; delete status.dataset.errorCode; }
      mountEntry(section);
    } catch (error) {
      section.hidden = true;
      section.__dimOfferReady = false;
      if (status) {
        status.dataset.errorCode = typeof error?.code === 'string' && /^[A-Z][A-Z0-9_]{0,79}$/.test(error.code) ? error.code : 'PREVIEW_UNAVAILABLE';
        status.hidden = false; status.textContent = section.hasAttribute('data-dim-offer-public')
          ? 'This storefront is unavailable. Return to its home page and try again.'
          : 'This saved storefront preview is unavailable. Sign in, or reopen the saved revision from Storefront Manager.';
      }
    } finally { section.__dimOfferLoading = false; }
  }

  const start = (scope) => {
    if (scope.matches?.('[data-dim-offer-preview]')) void mountOffer(scope);
    scope.querySelectorAll?.('[data-dim-offer-preview]').forEach(section => { void mountOffer(section); });
    if (scope.matches?.('[data-dim-offer-public]')) void mountOffer(scope);
    scope.querySelectorAll?.('[data-dim-offer-public]').forEach(section => { void mountOffer(section); });
    if (scope.matches?.(selector)) mount(scope);
    scope.querySelectorAll?.(selector).forEach(mount);
    if (scope.matches?.('[data-e-entry]')) mountEntry(scope);
    scope.querySelectorAll?.('[data-e-entry]').forEach(mountEntry);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => start(document), { once: true });
  else start(document);
  document.addEventListener('shopify:section:load', (event) => start(event.target));
  document.addEventListener('dim:runtime-authenticated', () => start(document));
})();
